import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './pages/auth/Login';
import { Verify } from './pages/auth/Verify';
import { MagicLinkSent } from './pages/auth/MagicLinkSent';
import { Error } from './pages/auth/Error';
import { Consent } from './pages/public/Consent';
import { Dashboard } from './pages/partner/Dashboard';
import { Referrals } from './pages/partner/Referrals';
import { useAuth } from './hooks/useAuth';

// Lazy-loaded routes (loaded on demand)
const Analytics = lazy(() => import('./pages/partner/Analytics'));
const Invoices = lazy(() => import('./pages/partner/Invoices'));
const MyLink = lazy(() => import('./pages/partner/MyLink'));
const DataExport = lazy(() => import('./pages/partner/DataExport'));
const Reports = lazy(() => import('./pages/partner/Reports'));
const Profile = lazy(() => import('./pages/partner/Profile'));
const IntakeForm = lazy(() => import('./pages/public/IntakeForm'));
const Track = lazy(() => import('./pages/public/Track'));
const PartnerLanding = lazy(() => import('./pages/public/PartnerLanding'));

// Lazy-loaded routes (loaded on demand)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const PartnersList = lazy(() => import('./pages/admin/PartnersList'));
const PartnerCreate = lazy(() => import('./pages/admin/PartnerCreate'));
const PartnerDetail = lazy(() => import('./pages/admin/PartnerDetail'));
const ReferralsList = lazy(() => import('./pages/admin/ReferralsList'));
const ReferralWorkflow = lazy(() => import('./pages/admin/ReferralWorkflow'));
const BulkApproval = lazy(() => import('./pages/admin/BulkApproval'));
const AuditLogs = lazy(() => import('./pages/admin/AuditLogs'));
const Settings = lazy(() => import('./pages/admin/Settings'));

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth/login?error=session_invalid" replace />;
  }
  
  return <>{children}</>;
}

// Public route (no auth needed)
function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-cream">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
        </div>
      }>
        <Routes>
          {/* Public routes */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/sent" element={<MagicLinkSent />} />
          <Route path="/auth/verify" element={<Verify />} />
          <Route path="/auth/admin/verify" element={<Verify />} />
          <Route path="/auth/error" element={<Error />} />
          <Route path="/auth/consent" element={<Consent />} />
          <Route path="/intake/:slug" element={<IntakeForm />} />
          <Route path="/track/:id" element={<Track />} />
          <Route path="/p/:slug" element={<PartnerLanding />} />
          
          {/* Protected partner routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute>
              <Referrals />
            </ProtectedRoute>
          } />
          <Route path="/my-link" element={
            <ProtectedRoute>
              <MyLink />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          } />
          
          <Route path="/export" element={
            <ProtectedRoute>
              <DataExport />
            </ProtectedRoute>
          } />
          
          <Route path="/reports" element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          
          {/* Admin routes (lazy-loaded) */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/partners" element={
            <ProtectedRoute>
              <PartnersList />
            </ProtectedRoute>
          } />
          <Route path="/admin/partners/new" element={
            <ProtectedRoute>
              <PartnerCreate />
            </ProtectedRoute>
          } />

          <Route path="/admin/partners/:id" element={
            <ProtectedRoute>
              <PartnerDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/referrals" element={
            <ProtectedRoute>
              <ReferralsList />
            </ProtectedRoute>
          } />
          <Route path="/admin/workflow" element={
            <ProtectedRoute>
              <ReferralWorkflow />
            </ProtectedRoute>
          } />
          <Route path="/admin/approvals" element={
            <ProtectedRoute>
              <BulkApproval />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/audit-logs" element={
            <ProtectedRoute>
              <AuditLogs />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;